import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";

const CONTACT_PHONE = "+1 403-336-6663";
const CONTACT_PHONE_HREF = "tel:+14033366663";
const CONTACT_EMAIL = "rohin@forterealty.ca";
const CONTACT_EMAIL_HREF = "mailto:rohin@forterealty.ca";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/listings", label: "Listings" },
  { to: "/residential", label: "Residential" },
  { to: "/commercial", label: "Commercial" },
  { to: "/medical-dental", label: "Medical & Dental" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const PublicSubHeader = () => (
  <div className="hidden md:block bg-navy-dark border-b border-white/5" data-testid="public-sub-header">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[11px] tracking-[0.18em] uppercase">
      <div className="text-white/50">Calgary, Alberta · Serving all of Alberta</div>
      <div className="flex items-center gap-6">
        <a href={CONTACT_PHONE_HREF} className="inline-flex items-center gap-1.5 text-gold hover:text-gold-light transition" data-testid="subheader-phone">
          <Phone className="w-3 h-3" strokeWidth={2.25} /> {CONTACT_PHONE}
        </a>
        <a href={CONTACT_EMAIL_HREF} className="hidden lg:inline-flex items-center gap-1.5 text-white/60 hover:text-gold transition" data-testid="subheader-email">
          <Mail className="w-3 h-3" strokeWidth={2.25} /> {CONTACT_EMAIL}
        </a>
      </div>
    </div>
  </div>
);

export const PublicHeader = () => {
  const [open, setOpen] = React.useState(false);
  return (
    <header className="sticky top-0 z-50 bg-navy/90 backdrop-blur-md border-b border-navy-light">
      <PublicSubHeader />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <Link to="/" className="block" data-testid="public-logo-link">
          <img
            src="/forte-logo.png"
            alt="Forte Realty"
            className="h-20 w-auto object-contain select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-gold" : "text-white/80 hover:text-gold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to="/crm/login" className="text-xs tracking-widest uppercase text-white/60 hover:text-gold transition" data-testid="agent-portal-link">
            Agent Portal
          </Link>
          <Link to="/appointment" className="btn-gold text-sm px-5 py-2.5" data-testid="book-consultation-btn">
            Book Consultation
          </Link>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-navy-light bg-navy">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-white/80 hover:text-gold py-2" data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}>
                {n.label}
              </Link>
            ))}
            <a href={CONTACT_PHONE_HREF} className="inline-flex items-center gap-2 text-gold py-2" data-testid="mobile-header-phone">
              <Phone className="w-4 h-4" /> {CONTACT_PHONE}
            </a>
            <Link to="/crm/login" onClick={() => setOpen(false)} className="text-white/60 py-2">Agent Portal</Link>
            <Link to="/appointment" onClick={() => setOpen(false)} className="btn-gold w-fit">Book Consultation</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export const PublicFooter = () => {
  const [email, setEmail] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setMsg("Thank you — you're on the list.");
      setEmail("");
    } catch { setMsg("Please try again."); }
  };
  return (
    <footer className="bg-navy-dark text-white/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="leading-none">
            <div className="font-display text-white text-4xl font-bold uppercase tracking-[0.18em]">Forte</div>
            <div className="font-display text-gold text-2xl font-bold uppercase tracking-[0.34em] mt-2">Realty</div>
          </div>
          <div className="overline mt-6">Residential · Commercial · Healthcare</div>
          <p className="mt-6 max-w-md leading-relaxed text-white/70">
            Specializing in exceptional homes, commercial properties, medical clinics,
            and existing dental practice sales. Confidential, credentialed, and committed.
          </p>
          <div className="mt-6 space-y-2 text-sm">
            <a href={CONTACT_PHONE_HREF} className="inline-flex items-center gap-2 text-white hover:text-gold transition" data-testid="footer-phone">
              <Phone className="w-4 h-4 text-gold" /> {CONTACT_PHONE}
            </a>
            <a href={CONTACT_EMAIL_HREF} className="flex items-center gap-2 text-white hover:text-gold transition" data-testid="footer-email">
              <svg className="w-4 h-4 text-gold" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
        <div>
          <div className="overline text-gold mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><a href="/listings" className="hover:text-gold">Listings</a></li>
            <li><a href="/residential" className="hover:text-gold">Residential</a></li>
            <li><a href="/commercial" className="hover:text-gold">Commercial</a></li>
            <li><a href="/medical-dental" className="hover:text-gold">Medical & Dental</a></li>
            <li><a href="/mortgage-calculator" className="hover:text-gold">Mortgage Calculator</a></li>
            <li><a href="/blog" className="hover:text-gold">Journal</a></li>
          </ul>
        </div>
        <div>
          <div className="overline text-gold mb-4">Newsletter</div>
          <p className="text-sm mb-4 text-white/70">Market briefings, trophy listings.</p>
          <form onSubmit={subscribe} className="flex" data-testid="newsletter-form">
            <input
              type="email" value={email} required placeholder="Email address"
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-gold"
              data-testid="newsletter-email-input"
            />
            <button className="bg-gold text-navy px-4 text-sm font-semibold" data-testid="newsletter-submit">→</button>
          </form>
          {msg && <div className="text-xs text-gold mt-2" data-testid="newsletter-message">{msg}</div>}
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
        © {new Date().getFullYear()} Forte Realty. Licensed real estate brokerage. All rights reserved.
      </div>
    </footer>
  );
};

export const PublicLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <PublicHeader />
    <main className="flex-1">{children}</main>
    <PublicFooter />
  </div>
);
